<script setup>
// Settings (phase 4.8): one column of the kit's SettingsSection + SettingRow — basics, features, system messages,
// guardrails and guidelines (D-P3, with polaris_v2), voice (administrators), and the danger zone — with one "Save" in the page header
// (R2-4-10 = A): on only when something changed; it saves the basics and the system messages together, in one call
// (two calls, each sending the whole config from the same snapshot, would undo each other's change); leaving the
// page (or switching assistant) with unsaved changes asks first. "The Fun Stuff" and its draft text left (human
// voice): the section is named after what it holds.
import { computed, reactive, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import SettingsSection from 'dashboard/components-next/polaris/cards/SettingsSection.vue';
import SettingRow from 'dashboard/components-next/polaris/cards/SettingRow.vue';
import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { isAdmin } = useAdmin();

const isPolarisV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.POLARIS_V2)
);
const route = useRoute();
const router = useRouter();
const store = useStore();

const deleteAssistantDialog = ref(null);
// SettingsSection puts its two columns side by side from the viewport's lg, but with both menus open a 1024px screen
// leaves this page ~560px: the rows (and the editor's toolbar) no longer fit. Below 56rem of its own width the page
// asks for the one-column form (`wide`). A contour: asked of the shared piece in the phase record.
const sections = ref(null);
const { width: sectionsWidth } = useElementSize(sections);
const narrow = computed(
  () => sectionsWidth.value > 0 && sectionsWidth.value < 896
);
const leaveDialog = ref(null);

const uiFlags = useMapGetter('polarisAssistants/getUIFlags');
const assistants = useMapGetter('polarisAssistants/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isSaving = computed(() => uiFlags.value.updatingItem);
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['polarisAssistants/getRecord'](assistantId.value)
);

// The form: what the two old forms held (AssistantBasicSettingsForm, AssistantSystemSettingsForm).
const state = reactive({
  name: '',
  productName: '',
  description: '',
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
    contactAttributes: false,
  },
  handoffMessage: '',
  resolutionMessage: '',
  instructions: '',
  // Personalidade e Avançado (Fase 7, spec §4.11): vivem no config e entram no prompt (ver personality_lines).
  tone: '',
  answerLength: '',
  languageMode: '',
  formality: '',
  orchestratorInstructions: '',
  temperature: '',
});
const snapshot = ref('');
const dirty = computed(() => JSON.stringify(state) !== snapshot.value);

const loadFrom = record => {
  const { config = {} } = record || {};
  Object.assign(state, {
    name: record?.name || '',
    productName: config.product_name || '',
    description: record?.description || '',
    features: {
      conversationFaqs: config.feature_faq || false,
      memories: config.feature_memory || false,
      citations: config.feature_citation || false,
      contactAttributes: config.feature_contact_attributes || false,
    },
    handoffMessage: config.handoff_message || '',
    resolutionMessage: config.resolution_message || '',
    instructions: config.instructions || '',
    tone: config.tone || '',
    answerLength: config.answer_length || '',
    languageMode: config.language_mode || '',
    formality: config.formality || '',
    orchestratorInstructions: config.orchestrator_instructions || '',
    temperature: config.temperature != null ? String(config.temperature) : '',
  });
  snapshot.value = JSON.stringify(state);
};

watch(assistant, record => loadFrom(record), { immediate: true });

const v$ = useVuelidate(
  // Name and description are required; the product name is optional (the backend does not require it, and an
  // older assistant without one must still be able to save the rest).
  {
    name: { required },
    description: { required },
  },
  state
);
const errorOf = (field, message) => (v$.value[field].$error ? message : '');

const featureRows = computed(() => [
  {
    id: 'conversationFaqs',
    kind: 'switch',
    label: t('POLARIS.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS'),
  },
  {
    id: 'memories',
    kind: 'switch',
    label: t('POLARIS.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES'),
  },
  {
    id: 'citations',
    kind: 'switch',
    label: t('POLARIS.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS'),
  },
  {
    id: 'contactAttributes',
    kind: 'switch',
    label: t('POLARIS.ASSISTANTS.FORM.FEATURES.ALLOW_CONTACT_ATTRIBUTES'),
  },
]);

// Fase 7 (spec §4.11): as opções fechadas da Personalidade (o vocabulário é o do backend — PERSONALITY_*).
const personalitySections = computed(() => [
  {
    id: 'tone',
    label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.TONE.LABEL'),
    options: [
      {
        value: '',
        label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.DEFAULT'),
      },
      ...['friendly', 'neutral', 'professional', 'playful'].map(value => ({
        value,
        label: t(
          `POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.TONE.${value.toUpperCase()}`
        ),
      })),
    ],
  },
  {
    id: 'answerLength',
    label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.LENGTH.LABEL'),
    options: [
      {
        value: '',
        label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.DEFAULT'),
      },
      ...['concise', 'standard', 'thorough'].map(value => ({
        value,
        label: t(
          `POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.LENGTH.${value.toUpperCase()}`
        ),
      })),
    ],
  },
  {
    id: 'languageMode',
    label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.LANGUAGE.LABEL'),
    options: [
      {
        value: '',
        label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.DEFAULT'),
      },
      ...['customer', 'pt-br'].map(value => ({
        value,
        label: t(
          `POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.LANGUAGE.${value === 'pt-br' ? 'PT_BR' : 'CUSTOMER'}`
        ),
      })),
    ],
  },
  {
    id: 'formality',
    label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.FORMALITY.LABEL'),
    options: [
      {
        value: '',
        label: t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.DEFAULT'),
      },
      ...['informal', 'formal'].map(value => ({
        value,
        label: t(
          `POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.FORMALITY.${value.toUpperCase()}`
        ),
      })),
    ],
  },
]);

// O template do assistente (Settings › Básicas): versão instalada + badge quando o catálogo tem uma mais nova.
const templateInfo = computed(() => assistant.value?.template || null);

const voiceRow = computed(() => ({
  id: 'polaris_assistants_voice_index',
  kind: 'action',
  label: t('POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.VOICE.TITLE'),
  help: t(
    'POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.VOICE.DESCRIPTION'
  ),
  actionLabel: t('POLARIS.PAGES.SETTINGS.OPEN'),
}));

const ruleRows = computed(() => [
  {
    id: 'polaris_assistants_guardrails_index',
    kind: 'action',
    label: t(
      'POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.TITLE'
    ),
    help: t(
      'POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.DESCRIPTION'
    ),
    actionLabel: t('POLARIS.PAGES.SETTINGS.OPEN'),
  },
  {
    id: 'polaris_assistants_guidelines_index',
    kind: 'action',
    label: t(
      'POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.TITLE'
    ),
    help: t(
      'POLARIS.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.DESCRIPTION'
    ),
    actionLabel: t('POLARIS.PAGES.SETTINGS.OPEN'),
  },
]);

// Guardrails and guidelines come with polaris_v2; the voice prompt (with its history) is for administrators.
const controlRows = computed(() => [
  ...(isPolarisV2Enabled.value ? ruleRows.value : []),
  ...(isAdmin.value ? [voiceRow.value] : []),
]);

const openControl = row => {
  router.push({
    name: row.id,
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};

const save = async () => {
  if (!(await v$.value.$validate())) return;
  const config = {
    ...assistant.value.config,
    product_name: state.productName,
    feature_faq: state.features.conversationFaqs,
    feature_memory: state.features.memories,
    feature_citation: state.features.citations,
    feature_contact_attributes: state.features.contactAttributes,
    handoff_message: state.handoffMessage,
    resolution_message: state.resolutionMessage,
    tone: state.tone || null,
    answer_length: state.answerLength || null,
    language_mode: state.languageMode || null,
    formality: state.formality || null,
    orchestrator_instructions: state.orchestratorInstructions || '',
  };
  if (state.temperature !== '') config.temperature = Number(state.temperature);
  if (!isPolarisV2Enabled.value) config.instructions = state.instructions;
  try {
    await store.dispatch('polarisAssistants/update', {
      id: assistantId.value,
      name: state.name,
      description: state.description,
      config,
    });
    snapshot.value = JSON.stringify(state);
    useAlert(t('POLARIS.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('POLARIS.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

// Leaving with unsaved changes asks first (a route change, or another assistant from the picker).
let pendingLeave = null;
const guard = next => {
  if (!dirty.value) {
    next();
    return;
  }
  pendingLeave = next;
  leaveDialog.value?.open();
};
onBeforeRouteLeave((to, from, next) => guard(next));
onBeforeRouteUpdate((to, from, next) => guard(next));
// The dialog closed without "Discard" (Keep editing, Escape, a click outside): stay.
const keepEditing = () => {
  pendingLeave?.(false);
  pendingLeave = null;
};
const discard = () => {
  loadFrom(assistant.value);
  const next = pendingLeave;
  pendingLeave = null;
  leaveDialog.value?.close();
  next?.();
};

const handleDelete = () => {
  deleteAssistantDialog.value.dialogRef.open();
};

const handleDeleteSuccess = () => {
  // The assistant is gone: nothing left to save.
  snapshot.value = JSON.stringify(state);
  const remainingAssistants = assistants.value.filter(
    a => a.id !== assistantId.value
  );

  if (remainingAssistants.length > 0) {
    const nextAssistant = remainingAssistants[0];
    router.push({
      name: 'polaris_assistants_settings_index',
      params: {
        accountId: route.params.accountId,
        assistantId: nextAssistant.id,
      },
    });
  } else {
    router.push({
      name: 'polaris_assistants_create_index',
      params: { accountId: route.params.accountId },
    });
  }
};
</script>

<template>
  <PageLayout
    preview="open"
    :header-title="t('POLARIS.ASSISTANTS.SETTINGS.HEADER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #headerActions>
      <Button size="sm" :disabled="!dirty || isSaving" @click="save">
        {{ t('POLARIS.DRAWER.SAVE') }}
      </Button>
    </template>
    <template #body>
      <div ref="sections" class="grid gap-6">
        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.FORM.SECTIONS.BASIC_INFO')"
          :description="t('POLARIS.PAGES.SETTINGS.BASICS_DESCRIPTION')"
        >
          <SettingRow
            v-model="state.name"
            :row="{
              id: 'name',
              label: t('POLARIS.ASSISTANTS.FORM.NAME.LABEL'),
              placeholder: t('POLARIS.ASSISTANTS.FORM.NAME.PLACEHOLDER'),
            }"
            :error="errorOf('name', t('POLARIS.ASSISTANTS.FORM.NAME.ERROR'))"
          />
          <SettingRow
            v-model="state.productName"
            :row="{
              id: 'product',
              label: t('POLARIS.ASSISTANTS.FORM.PRODUCT_NAME.LABEL'),
              placeholder: t(
                'POLARIS.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER'
              ),
            }"
          />
          <Editor
            v-model="state.description"
            :max-length="500"
            :label="t('POLARIS.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="t('POLARIS.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
            :message="
              errorOf(
                'description',
                t('POLARIS.ASSISTANTS.FORM.DESCRIPTION.ERROR')
              )
            "
            :message-type="v$.description.$error ? 'error' : 'info'"
            class="z-0"
          />
          <div
            v-if="templateInfo"
            class="flex items-center gap-2 text-sm text-n-slate-12"
            data-testid="template-row"
          >
            <span class="text-n-slate-11">
              {{ t('POLARIS.ASSISTANTS.SETTINGS.TEMPLATE.LABEL') }}
            </span>
            <span>
              {{
                t('POLARIS.ASSISTANTS.SETTINGS.TEMPLATE.VALUE', {
                  key: templateInfo.key,
                  version: templateInfo.version,
                })
              }}
            </span>
            <Badge v-if="templateInfo.update_available" variant="warning">
              {{ t('POLARIS.ASSISTANTS.SETTINGS.TEMPLATE.UPDATE_AVAILABLE') }}
            </Badge>
          </div>
        </SettingsSection>

        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.FORM.FEATURES.TITLE')"
          :description="t('POLARIS.PAGES.SETTINGS.FEATURES_DESCRIPTION')"
        >
          <SettingRow
            v-for="row in featureRows"
            :key="row.id"
            v-model="state.features[row.id]"
            :row="row"
          />
        </SettingsSection>

        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.FORM.SECTIONS.SYSTEM_MESSAGES')"
          :description="
            t('POLARIS.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.DESCRIPTION')
          "
        >
          <Editor
            v-model="state.handoffMessage"
            :label="t('POLARIS.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL')"
            :placeholder="
              t('POLARIS.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')
            "
            class="z-0"
          />
          <Editor
            v-model="state.resolutionMessage"
            :label="t('POLARIS.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL')"
            :placeholder="
              t('POLARIS.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')
            "
            class="z-0"
          />
          <Editor
            v-if="!isPolarisV2Enabled"
            v-model="state.instructions"
            :label="t('POLARIS.ASSISTANTS.FORM.INSTRUCTIONS.LABEL')"
            :placeholder="t('POLARIS.ASSISTANTS.FORM.INSTRUCTIONS.PLACEHOLDER')"
            :max-length="20000"
            class="z-0"
          />
        </SettingsSection>

        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.TITLE')"
          :description="
            t('POLARIS.ASSISTANTS.SETTINGS.PERSONALITY.DESCRIPTION')
          "
        >
          <div
            v-for="row in personalitySections"
            :key="row.id"
            class="flex flex-col gap-1"
          >
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ row.label }}
            </label>
            <ComboBox
              v-model="state[row.id]"
              :options="row.options"
              class="max-w-md [&>div>button]:bg-n-alpha-black2"
            />
          </div>
        </SettingsSection>

        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.TITLE')"
          :description="t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.DESCRIPTION')"
        >
          <Editor
            v-model="state.orchestratorInstructions"
            :max-length="2000"
            :label="
              t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.INSTRUCTIONS.LABEL')
            "
            :placeholder="
              t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.INSTRUCTIONS.PLACEHOLDER')
            "
            class="z-0"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.INSTRUCTIONS.NOTE') }}
          </p>
          <Input
            v-model="state.temperature"
            type="number"
            min="0"
            max="1"
            step="0.1"
            class="max-w-40"
            :label="t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.TEMPERATURE.LABEL')"
            :message="
              t('POLARIS.ASSISTANTS.SETTINGS.ADVANCED.TEMPERATURE.NOTE')
            "
            message-type="info"
          />
        </SettingsSection>

        <SettingsSection
          v-if="controlRows.length"
          :wide="narrow"
          :title="t('POLARIS.PAGES.SETTINGS.CONTROLS_TITLE')"
          :description="t('POLARIS.PAGES.SETTINGS.CONTROLS_DESCRIPTION')"
        >
          <SettingRow
            v-for="row in controlRows"
            :key="row.id"
            :row="row"
            @action="openControl"
          />
        </SettingsSection>

        <SettingsSection
          v-if="assistant?.id"
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.SETTINGS.DELETE.TITLE')"
          :description="t('POLARIS.ASSISTANTS.SETTINGS.DELETE.DESCRIPTION')"
          data-danger-zone
        >
          <div>
            <Button
              variant="destructive"
              size="sm"
              class="max-w-full"
              @click="handleDelete"
            >
              <span class="min-w-0 truncate">
                {{
                  t('POLARIS.ASSISTANTS.SETTINGS.DELETE.BUTTON_TEXT', {
                    assistantName: assistant.name,
                  })
                }}
              </span>
            </Button>
          </div>
        </SettingsSection>
      </div>
    </template>
    <DeleteDialog
      v-if="assistant"
      ref="deleteAssistantDialog"
      :entity="assistant"
      type="Assistants"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
    <Dialog
      ref="leaveDialog"
      type="alert"
      width="md"
      :title="t('POLARIS.DRAWER.DISCARD_TITLE')"
      :description="t('POLARIS.DRAWER.DISCARD_TEXT')"
      :cancel-button-label="t('POLARIS.DRAWER.KEEP_EDITING')"
      :confirm-button-label="t('POLARIS.DRAWER.DISCARD')"
      @confirm="discard"
      @close="keepEditing"
    />
  </PageLayout>
</template>
